import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import OperatorLayout from "./OperatorLayout";
import { Button } from "@/components/ui/button";
import { useOperatorApi } from "@/hooks/useOperatorApi";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export default function OperatorOpportunities() {
  const { t } = useTranslation();
  const { operatorFetch } = useOperatorApi();
  const [opps, setOpps] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<any>(null);
  const [filter, setFilter] = useState<string>("all");

  useEffect(() => { loadOpps(); }, [filter]);

  async function loadOpps() {
    setLoading(true);
    let query = supabase.from("opportunities")
      .select("*, requests(category, urgency, location, requester_company, requester_email), provider_profiles(company_name)")
      .order("created_at", { ascending: false }).limit(100);

    if (filter === "new") query = query.eq("status", "new");
    if (filter === "in_review") query = query.eq("status", "in_review");
    if (filter === "failed") query = query.in("delivery_state", ["failed", "FAILED", "FAILED_DELIVERY"]);
    if (filter === "hold") query = query.in("delivery_state", ["hold", "HOLD", "BLOCKED_BILLING"]);

    const { data } = await query;
    setOpps(data || []);
    setLoading(false);
  }

  async function resend(opportunityId: string) {
    try {
      await operatorFetch(`requests/${selected?.request_id}/rematch`, { method: "POST" });
      toast.success(t("op.oppResendSuccess"));
      loadOpps();
    } catch (err: any) {
      toast.error(err.message);
    }
  }

  const FILTER_LABELS: Record<string, string> = {
    all: t("op.filterAll"),
    new: t("op.oppFilterNew"),
    in_review: t("op.oppFilterInReview"),
    failed: t("op.oppFilterFailed"),
    hold: t("op.oppFilterHold"),
  };

  return (
    <OperatorLayout title={t("op.navOpportunities")} subtitle={t("op.oppSubtitle")}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          <div className="flex gap-2 flex-wrap">
            {["all", "new", "in_review", "failed", "hold"].map((f) => (
              <button key={f} onClick={() => setFilter(f)}
                className={cn("px-3 py-1.5 text-xs border transition-colors",
                  filter === f ? "border-accent bg-accent/5 text-foreground" : "border-border text-muted-foreground hover:text-foreground"
                )}>
                {FILTER_LABELS[f]}
              </button>
            ))}
          </div>

          <div className="border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-3 text-micro font-normal">{t("op.oppColNumber")}</th>
                  <th className="text-left p-3 text-micro font-normal">{t("op.oppColCategory")}</th>
                  <th className="text-left p-3 text-micro font-normal">{t("op.oppColProvider")}</th>
                  <th className="text-left p-3 text-micro font-normal">{t("op.oppColScore")}</th>
                  <th className="text-left p-3 text-micro font-normal">{t("op.oppColStatus")}</th>
                  <th className="text-left p-3 text-micro font-normal">{t("op.oppColDelivery")}</th>
                  <th className="text-left p-3 text-micro font-normal">{t("op.oppColPrice")}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="p-8 text-center text-caption">{t("common.loading")}</td></tr>
                ) : opps.length === 0 ? (
                  <tr><td colSpan={7} className="p-8 text-center text-caption">{t("op.oppEmpty")}</td></tr>
                ) : (
                  opps.map((opp) => (
                    <tr key={opp.id}
                      className={cn("border-b border-border cursor-pointer hover:bg-secondary/30 transition-colors",
                        selected?.id === opp.id && "bg-accent/5")}
                      onClick={() => setSelected(opp)}>
                      <td className="p-3 font-medium">{opp.opportunity_number}</td>
                      <td className="p-3 text-caption">{opp.requests?.category || "—"}</td>
                      <td className="p-3 text-caption">{opp.provider_profiles?.company_name || "—"}</td>
                      <td className="p-3">{opp.match_score}%</td>
                      <td className="p-3"><span className="text-xs px-2 py-0.5 border border-border">{opp.status}</span></td>
                      <td className="p-3">
                        <span className={cn("text-xs px-2 py-0.5 border",
                          (opp.delivery_state === "sent" || opp.delivery_state === "SENT" || opp.delivery_state === "DELIVERED") ? "border-accent/30 text-accent" :
                          (opp.delivery_state === "failed" || opp.delivery_state === "FAILED" || opp.delivery_state === "FAILED_DELIVERY") ? "border-destructive/30 text-destructive" :
                          "border-border text-muted-foreground"
                        )}>
                          {opp.delivery_state}
                        </span>
                      </td>
                      <td className="p-3 price-display">{opp.price} €</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Detail sidebar */}
        <div>
          {selected ? (
            <div className="border border-border bg-card p-6 space-y-4 sticky top-8">
              <h3 className="text-subheadline">{selected.opportunity_number}</h3>
              <div className="space-y-3">
                {[
                  [t("op.oppColCategory"), selected.requests?.category],
                  [t("op.oppDetailUrgency"), selected.requests?.urgency],
                  [t("op.oppColProvider"), selected.provider_profiles?.company_name],
                  [t("op.oppDetailCompany"), selected.requests?.requester_company],
                  [t("op.oppDetailEmail"), selected.requests?.requester_email],
                  [t("op.oppDetailLocation"), selected.requests?.location],
                  [t("op.oppColScore"), `${selected.match_score}%`],
                  [t("op.oppColPrice"), `${selected.price} €`],
                  [t("op.oppColStatus"), selected.status],
                  [t("op.oppColDelivery"), selected.delivery_state],
                  [t("op.oppDetailHoldReason"), selected.hold_reason || "—"],
                ].map(([label, value]) => (
                  <div key={label as string}>
                    <span className="text-micro block mb-0.5">{label}</span>
                    <p className="text-sm">{value || "—"}</p>
                  </div>
                ))}
              </div>
              {(selected.delivery_state === "failed" || selected.delivery_state === "FAILED" || selected.delivery_state === "FAILED_DELIVERY") && (
                <Button variant="commit" size="sm" onClick={() => resend(selected.id)}>
                  {t("op.oppResend")}
                </Button>
              )}
            </div>
          ) : (
            <div className="border border-dashed border-border p-8 text-center">
              <p className="text-caption">{t("op.oppSelectHint")}</p>
            </div>
          )}
        </div>
      </div>
    </OperatorLayout>
  );
}